import { Routes } from '@angular/router';
import { AppsSelectorComponent } from '@app/shared/components/index';
import { AuthGuard } from '@common/auth/services/index';
import { platformOwnerGuard, providerSpaceGuard, tenantManagerGuard } from '@common/auth/services/space.guards';
import { AuthedHomeRedirectGuard } from '@common/auth/services/authed-home-redirect.guard';
import { kioskLockAppGuard } from '@app/features/erp-apps/restaurant/core/kiosk-lock.rule';

/*
 * kzd r1 polish P5: a device locked to the restaurant kiosk opens no signed-in top-level address either — Settings,
 * the dashboard, the profile, the admin pages, /pos, /owner — by URL or bookmark: `kioskLockAppGuard` (the provider
 * app's own rule, not a second one) sits LAST in each one's `canActivate`, after AuthGuard, so a signed-out visitor
 * still goes to the login first. Exempt on purpose: the entry flow (`''`, the AuthModule's /login, /signup, …), the
 * public pages (privacy, terms, page-not-found) and the consumer space — a kiosk whose session expired must be able
 * to sign in again, and the lock then sends it back to the kiosk. Redirect-only routes are covered by their target.
 * Pinned by a walk of this table in `kiosk-lock.rule.spec.ts`.
 */
export const productRoutes: Routes = [

  // { path: '', redirectTo: 'web', pathMatch: 'full' },
  // { path: 'welcome', redirectTo: 'welcome', pathMatch: 'full' },
  // The entry flow goes straight to login (kz2 F1). The walkthrough → welcome → login chain is
  // retired; the walkthrough opens from Login and Signup. AuthedHomeRedirectGuard sits on /login.
  { path: '', redirectTo: 'login', pathMatch: 'full' },
  {
    // kz2 F4: the design catalog (/dev/catalog). Non-production builds only (devGuard); no AuthGuard.
    path: 'dev',
    loadChildren: () => import('@app/features/dev/dev.module').then(m => m.DevModule),
    canActivate: [kioskLockAppGuard]
  },
  {
    path: 'page-not-found',
    loadChildren: () => import('@common/page-not-found/page-not-found.module').then(m => m.PageNotFoundModule)
  },

  {
    path: 'privacy-policy',
    loadChildren: () => import('@common/privacy-policy/privacy-policy.module').then(m => m.PrivacyPolicyModule)
  },
  {
    path: 'terms-of-service',
    loadChildren: () => import('@common/terms-of-service/terms-of-service.module').then(m => m.TermsOfServiceModule)
  },
  {
    path: 'dashboard',
    loadChildren: () => import('@common/dashboard/dashboard.module').then(m => m.DashboardModule),
    data: { preload: false, delay: true },
    // kz2 M12 R4: owners, managers and the platform only.
    canActivate: [AuthGuard, tenantManagerGuard, kioskLockAppGuard]
  },
  {
    path: 'account/delete',
    loadChildren: () => import('@common/account-delete/account-delete.module').then(m => m.AccountDeleteModule),
    data: { preload: false, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },


  

  {
    path: 'data-dictionary',
    loadChildren: () => import('@common/data-dictionary/server/data-dictionary.module').then(m => m.DataDictionaryModule),
    data: { preload: false, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },
  {
    // kz2 A1 follow-up: the Tenants admin is the platform's (its rules refuse anyone else).
    path: 'tenants',
    loadChildren: () => import('@common/tenants/server/tenants.module').then(m => m.TenantsModule),
    data: { preload: false, delay: true },
    canActivate: [AuthGuard, platformOwnerGuard, kioskLockAppGuard]
  },
  {
    path: 'push-notification',
    loadChildren: () => import('@common/push-notification/server/push-notification.module').then(m => m.PushNotificationModule),
    data: { preload: true, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },

  {
    path: 'content-category',
    loadChildren: () => import('@common/content-category/content-category.module').then(m => m.ContentCategoryModule),
    data: { preload: true, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },

  {
    path: 'media-content',
    loadChildren: () => import('@common/media-content/media-content.module').then(m => m.MediaContentModule),
    data: { preload: true, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },

  {
    path: 'user-profile',
    // loadChildren: () => import('../client/users-client.module').then(m => m.UsersClientModule),
    loadChildren: () => import('@common/users/common/user-account/user-account.module').then(m => m.UserAccountModule),
    data: { preload: true, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },
  {
    path: 'users',
    loadChildren: () => import('@common/users/server/users.module').then(m => m.UsersModule),
    data: { preload: true, delay: false },
    // kz2 M12 R4: owners, managers and the platform only.
    canActivate: [AuthGuard, tenantManagerGuard, kioskLockAppGuard]
  },

  {
    path: 'settings',
    loadChildren: () => import('@common/settings/client/settings.module').then(m => m.SettingsModule),
    data: { preload: false, delay: false },
    canActivate: [AuthGuard, kioskLockAppGuard]
  },
  {
    path: 'owner/app/audit',
    loadChildren: () => import('@common/audit-data/server/audit-data.module').then(m => m.AuditDataModule),
    data: { preload: false, delay: false },
    // kz2 M12 R4: the platform's (it had no guard).
    canActivate: [AuthGuard, platformOwnerGuard, kioskLockAppGuard]
  },

  //consumer routes 
  {
    path: 'consumer',
    loadChildren: () => import('@app/features/consumer-app/consumer-app.module').then(m => m.ConsumerAppModule),
    data: { preload: false, delay: false },
    //canActivate: [AuthGuard]
  },
  {
    path: 'pos',
    loadChildren: () => import('@app/features/erp-apps/point-of-sale/point-of-sale.module').then(m => m.PointOfSaleModule),
    data: { preload: false, delay: false },
    canActivate: [kioskLockAppGuard]
  },
  {
    path: 'service-booking',
    loadChildren: () => import('@app/features/erp-apps/service-booking/service-booking.module').then(m => m.ServiceBookingModule),
    data: { preload: false, delay: false },
    canActivate: [kioskLockAppGuard]
  },

  //service provider routes
  {
    path: 'provider',
    loadChildren: () => import('@app/features/provider-app/provider-app.module').then(m => m.ProviderAppModule),
    data: { preload: true, delay: false },
    // kz2 M12 R4: a pure consumer typing /provider/... goes to /consumer.
    canActivate: [AuthGuard, providerSpaceGuard, kioskLockAppGuard]
  },

  //owner routes
  {
    path: 'owner',
    loadChildren: () => import('@app/features/owner-app/owner-app.module').then(m => m.OwnerAppModule),
    data: { preload: false, delay: false },
    // kz2 M12 R4: the platform administrator only (it had no guard).
    canActivate: [AuthGuard, platformOwnerGuard, kioskLockAppGuard]
  },
  
  
  //business specific routes  
  {
    path: 'forms-app-builder',
    loadChildren: () => import('@app/features/forms-app-manager/server/forms-app-project/forms-app-project.module').then(m => m.FormsAppProjectModule),
    data: { preload: false, delay: false },
   // canActivate: [AuthGuard]
    canActivate: [kioskLockAppGuard]
  },
  // PLAN-forms-data-sources P6: the Reference Data page is retired — its old URL opens Data sources.
  { path: 'manage-app-reference-data', redirectTo: '/provider/app/form-data-sources', pathMatch: 'full' },
    {
      path: 'app-selector',
      component: AppsSelectorComponent,
      // kz2 M12 R3/R4: signed in (it had no guard: a visitor saw the page), owners and managers only.
      canActivate: [AuthGuard, tenantManagerGuard, kioskLockAppGuard]
      // data:{userRoleBasedAppName: 'provider-app', className :''}
    },
  //,  { path: '**', redirectTo: '/welcome', pathMatch: 'full' }
  {
    /* The legacy Bluetooth device manager is retired (A3 follow-ups, owner, 5 Oct 2026): printing never
       read its global `ble-devices` list. A bookmark lands where this device's printer is chosen now. */
    path: 'ble-devices-manager',
    redirectTo: '/settings?section=device',
    pathMatch: 'full',
  }

  // NO `**` here (kz2 M12 N-2): these routes are registered BEFORE the eager modules' own routes
  // (AuthModule's /login, /signup, /walkthrough…; Tags; Tenants), so a `**` in this list shadows them
  // all — why the old `** → /welcome` above was commented out. The catch-all is
  // `app-fallback-routing.module.ts`, imported LAST in AppModule.

];
